import React from "react";
import StrokeTextAnimation from "./StrokeAnimation";

const CustomHeading = ({ text, style = {}, icon: Icon }) => {
  /* =========================================================
     DEFAULT CONFIGURATION
  ========================================================= */

  const config = {
    // Container
    height: "190px",
    width: "100%",
    minHeight: undefined,
    maxHeight: undefined,

    background: "#fff",
    borderRadius: "16px",
    shadow: "0 8px 25px rgba(0,0,0,0.12)",

    // Gradient
    gradient: true,
    color: ["#063b87", "#0a57bb", "#087344"],
    gradientDirection: "120deg",

    // Heading
    headingColor: "#ffffff",
    headingSize: undefined,
    headingWidth: "min(520px, 85%)",
    headingWeight: 700,

    // Subtitle
    subtitleColor: "#f4f7fb",
    subtitleSize: "clamp(0.72rem, 1.5vw, 0.95rem)",
    subtitleMaxWidth: "650px",
    subtitleLineHeight: 1.5,
    subtitleWeight: 400,
    subtitleLetterSpacing: "0.3px",

    // Content
    contentGap: "4px",
    contentPadding: "10px",

    // Decoration
    showGlow: true,
    showLeftGlow: true,
    showRailway: true,
    decorationOpacity: 0.18,

    // Railway
    railwayWidth: "220px",
    railwayHeight: "60px",
    railwayTop: "20px",
    railwayRight: "30px",

    // Glow
    rightGlowSize: "280px",
    rightGlowRight: "-100px",
    rightGlowTop: "-160px",

    leftGlowSize: "180px",
    leftGlowLeft: "-80px",
    leftGlowTop: "-100px",

    // Section
    sectionPaddingX: undefined,
    sectionPaddingY: "3",

    // Animation
    animation: true,

    ...style,
  };

  /* =========================================================
     COLORS
  ========================================================= */

  const colors = config.color || ["#063b87", "#0a57bb", "#087344"];

  /* =========================================================
     GRADIENT
  ========================================================= */

  const background = config.gradient
    ? `linear-gradient(
        ${config.gradientDirection},
        ${colors[0]} 0%,
        ${colors[1]} 45%,
        ${colors[2]} 100%
      )`
    : colors[0];

  return (
    <div>
      <section
        className={`px-2 ${
          config.sectionPaddingX === undefined ? "px-md-3" : ""
        } py-${config.sectionPaddingY}`}
      >
        <div
          className="position-relative overflow-hidden"
          style={{
            width: config.width,
            height: config.height,

            minHeight: config.minHeight,
            maxHeight: config.maxHeight,

            borderRadius: config.borderRadius,
            boxShadow: config.shadow,

            background: config.background,
          }}
        >
          {/* =====================================================
              GRADIENT BACKGROUND
          ===================================================== */}

          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background,
            }}
          />

          {/* =====================================================
              RIGHT GLOW
          ===================================================== */}

          {config.showGlow && (
            <div
              className="position-absolute"
              style={{
                width: config.rightGlowSize,
                height: config.rightGlowSize,

                borderRadius: "50%",

                right: config.rightGlowRight,
                top: config.rightGlowTop,

                background: "rgba(255,255,255,0.10)",

                filter: "blur(2px)",

                pointerEvents: "none",
              }}
            />
          )}

          {/* =====================================================
              LEFT GLOW
          ===================================================== */}

          {config.showLeftGlow && (
            <div
              className="position-absolute"
              style={{
                width: config.leftGlowSize,
                height: config.leftGlowSize,

                borderRadius: "50%",

                left: config.leftGlowLeft,
                top: config.leftGlowTop,

                background: "rgba(255,255,255,0.06)",

                pointerEvents: "none",
              }}
            />
          )}

          {/* =====================================================
              RAILWAY DECORATION
          ===================================================== */}

          {config.showRailway && (
            <div
              className="position-absolute d-none d-md-block"
              style={{
                right: config.railwayRight,
                top: config.railwayTop,

                width: config.railwayWidth,
                height: config.railwayHeight,

                opacity: config.decorationOpacity,

                pointerEvents: "none",
              }}
            >
              <Icon
                style={{
                  position: "absolute",
                  width: "50%",
                  height: "50px",
                  color: "rgba(185, 185, 185, 0.77)",
                  background: "#ffffff00",
                  top: "18px",
                  transform: "rotate(-18deg)",
                }}
              />{" "}
              <Icon
                style={{
                  position: "absolute",
                  width: "50%",
                  height: "100px",
                  right: "-65px",
                  marginTop: "-40px",
                  color: "rgba(185, 185, 185, 0.57)",
                  background: "#ffffff00",
                  top: "18px",
                  transform: "rotate(-8deg)",
                }}
              />
              <Icon
                style={{
                  position: "absolute",
                  width: "50%",
                  left: "35px",
                  height: "40px",
                  color: "rgb(255, 255, 255)",
                  background: "#ffffff00",
                  top: "18px",
                  transform: "rotate(-8deg)",
                }}
              />{" "}
              <Icon
                style={{
                  position: "absolute",
                  width: "50%",
                  left: "65px",
                  marginTop: "-20px",
                  height: "30px",
                  color: "rgb(212, 210, 210)",
                  background: "#ffffff00",
                  top: "18px",
                  transform: "rotate(28deg)",
                }}
              />
            </div>
          )}

          {/* =====================================================
              CONTENT
          ===================================================== */}

          <div
            className="
              position-relative
              d-flex
              flex-column
              align-items-center
              justify-content-center
              text-center
              w-100
              h-100
            "
            style={{
              zIndex: 2,
              gap: config.contentGap,
              padding: config.contentPadding,
            }}
          >
            {/* =================================================
                HEADING
            ================================================= */}

            <div
              className="fw-bold"
              style={{
                width: config.headingWidth,

                fontSize: config.headingSize,

                fontWeight: config.headingWeight,

                color: config.headingColor,
              }}
            >
              {config.animation ? (
                <StrokeTextAnimation text={text?.heading} />
              ) : (
                text?.heading
              )}
            </div>

            {/* =================================================
                SUBTITLE
            ================================================= */}

            {text?.subText && (
              <div
                className=""
                style={{
                  color: config.subtitleColor,

                  fontSize: config.subtitleSize,

                  fontWeight: config.subtitleWeight,

                  letterSpacing: config.subtitleLetterSpacing,

                  maxWidth: config.subtitleMaxWidth,

                  lineHeight: config.subtitleLineHeight,
                  marginTop: "-20px",
                }}
              >
                {text.subText}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomHeading;
